import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { CreditCard, MapPin, Phone, AlertTriangle, CheckCircle, MessageCircle, ExternalLink, Utensils, Tractor, Bike, User, RefreshCw, Wrench } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const AdminSettingsManagement = () => {
  const { user, setUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // WhatsApp Support state - Customer Support
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [supportMessage, setSupportMessage] = useState("I need help");
  const [customerSupportName, setCustomerSupportName] = useState("");
  
  // Rider Support state
  const [riderWhatsappNumber, setRiderWhatsappNumber] = useState("");
  const [riderSupportMessage, setRiderSupportMessage] = useState("I need help with my ride");
  const [riderSupportName, setRiderSupportName] = useState("");
  
  const [savingSupport, setSavingSupport] = useState(false);
  
  // Tiered Notification Settings
  const [tier1Distance, setTier1Distance] = useState(2);
  const [tier2Delay, setTier2Delay] = useState(15);
  const [savingTieredSettings, setSavingTieredSettings] = useState(false);
  
  // External Services state
  const [externalServices, setExternalServices] = useState({
    bizil_food_url: "",
    bizil_food_enabled: true,
    farmster_url: "",
    farmster_enabled: false
  });
  const [savingExternal, setSavingExternal] = useState(false);
  
  // Phone change state
  const [showPhoneChangeDialog, setShowPhoneChangeDialog] = useState(false);
  const [phoneChangeStep, setPhoneChangeStep] = useState(1); // 1: enter new phone, 2: confirm, 3: OTP
  const [newPhone, setNewPhone] = useState("");
  const [confirmPhone, setConfirmPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [changingPhone, setChangingPhone] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  
  // Sync utility state
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [syncDateFrom, setSyncDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 3);  // Default to 3 days ago for faster processing
    return d.toISOString().split('T')[0];
  });
  const [syncDateTo, setSyncDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchSettings();
    fetchSupportSettings();
    fetchExternalServices();
    fetchTieredNotificationSettings();
  }, []);

  const fetchTieredNotificationSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/tiered-notification/${user.id}`);
      setTier1Distance(res.data.tier1_distance_km || 2);
      setTier2Delay(res.data.tier2_delay_seconds || 15);
    } catch (err) {
      // Use defaults
    }
  };

  const handleSaveTieredSettings = async () => {
    setSavingTieredSettings(true);
    try {
      await axios.put(`${API}/settings/tiered-notification/${user.id}`, {
        tier1_distance_km: parseFloat(tier1Distance) || 2,
        tier2_delay_seconds: parseInt(tier2Delay) || 15
      });
      toast.success("Notification settings saved!");
    } catch (err) {
      toast.error("Failed to save notification settings");
    } finally {
      setSavingTieredSettings(false);
    }
  };

  const fetchExternalServices = async () => {
    try {
      const res = await axios.get(`${API}/settings/external-services/${user.id}`);
      setExternalServices(res.data);
    } catch (err) {
      // Use defaults
    }
  };

  const handleSaveExternalServices = async () => {
    setSavingExternal(true);
    try {
      await axios.put(`${API}/settings/external-services/${user.id}`, externalServices);
      toast.success("External services updated!");
    } catch (err) {
      toast.error("Failed to update external services");
    } finally {
      setSavingExternal(false);
    }
  };

  const fetchSupportSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/support/${user.id}`);
      setWhatsappNumber(res.data.whatsapp_number || "");
      setSupportMessage(res.data.support_message || "I need help");
      setCustomerSupportName(res.data.customer_support_name || "");
      setRiderWhatsappNumber(res.data.rider_whatsapp_number || "");
      setRiderSupportMessage(res.data.rider_support_message || "I need help with my ride");
      setRiderSupportName(res.data.rider_support_name || "");
    } catch (err) {
      // No support settings found - using defaults
    }
  };

  const handleSaveSupport = async () => {
    setSavingSupport(true);
    try {
      await axios.put(`${API}/settings/support/${user.id}`, {
        whatsapp_number: whatsappNumber || null,
        support_message: supportMessage,
        customer_support_name: customerSupportName || null,
        rider_whatsapp_number: riderWhatsappNumber || null,
        rider_support_message: riderSupportMessage,
        rider_support_name: riderSupportName || null
      });
      toast.success("Support settings saved!");
    } catch (err) {
      toast.error("Failed to save support settings");
    } finally {
      setSavingSupport(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/${user.id}/settings`);
      setSettings(res.data);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/${user.id}/settings`, {
        payment_mode: settings.payment_mode,
        razorpay_key_id: settings.razorpay_key_id || null,
        razorpay_key_secret: settings.razorpay_key_secret || null,
        map_provider: settings.map_provider,
        google_maps_api_key: settings.google_maps_api_key || null
      });
      toast.success("Settings saved successfully!");
      fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const resetPhoneChangeDialog = () => {
    setShowPhoneChangeDialog(false);
    setPhoneChangeStep(1);
    setNewPhone("");
    setConfirmPhone("");
    setOtp("");
    setShowFinalConfirm(false);
  };

  const handleInitiatePhoneChange = async () => {
    if (newPhone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return;
    }
    if (newPhone !== confirmPhone) {
      toast.error("Phone numbers don't match");
      return;
    }
    
    setShowFinalConfirm(true);
  };

  const handleConfirmAndSendOTP = async () => {
    setChangingPhone(true);
    try {
      const res = await axios.post(`${API}/admin/${user.id}/change-phone/initiate`, {
        new_phone: newPhone,
        confirm_new_phone: confirmPhone
      });
      toast.success(res.data.message);
      setShowFinalConfirm(false);
      setPhoneChangeStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate phone change");
    } finally {
      setChangingPhone(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }
    
    setChangingPhone(true);
    try {
      const res = await axios.post(`${API}/admin/${user.id}/change-phone/verify`, {
        new_phone: newPhone,
        otp: otp
      });
      toast.success("Phone number changed successfully!");
      
      // Update user context with new phone
      if (setUser) {
        setUser({ ...user, phone: newPhone });
      }
      
      resetPhoneChangeDialog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to verify OTP");
    } finally {
      setChangingPhone(false);
    }
  };

  // Sync food delivery orders utility
  const handleSyncFoodDeliveryOrders = async () => {
    setSyncing(true);
    setLastSyncResult(null);
    try {
      const params = new URLSearchParams();
      if (syncDateFrom) params.append('date_from', syncDateFrom);
      if (syncDateTo) params.append('date_to', syncDateTo);
      params.append('limit', '200');  // Reduced limit for reliability
      
      const res = await axios.post(`${API}/admin/utils/sync-food-delivery-orders?${params.toString()}`, {}, { timeout: 120000 });
      setLastSyncResult(res.data);
      if (res.data.synced_rides_to_qc > 0 || res.data.synced_qc_to_rides > 0) {
        toast.success(`Synced ${res.data.synced_rides_to_qc || 0} rides→qc, ${res.data.synced_qc_to_rides || 0} qc→rides`);
      } else {
        toast.info("All orders in this date range are already in sync");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || "Sync failed - please try again";
      toast.error(errorMsg);
      setLastSyncResult({ error: errorMsg });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-management">
      <h1 className="text-2xl font-bold text-foreground mb-6">Platform Settings</h1>

      <div className="space-y-6">
        {/* Account & Phone Number Section */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Account & Login</h3>
              <p className="text-sm text-muted-foreground">Manage your login phone number</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Current Login Phone</p>
                <p className="font-mono text-lg font-medium">{user?.phone || "Not set"}</p>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowPhoneChangeDialog(true)}
                data-testid="change-phone-btn"
              >
                <Phone className="w-4 h-4 mr-2" />
                Change Phone
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Changing your phone number will not affect your data, cities, drivers, or any other settings. 
              You will use the new number to log in.
            </p>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Payment Options</h3>
              <p className="text-sm text-muted-foreground">Configure how customers pay for rides</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Payment Mode</Label>
              <Select 
                value={settings?.payment_mode || 'both'} 
                onValueChange={(v) => setSettings({...settings, payment_mode: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cod">Cash on Delivery (COD) Only</SelectItem>
                  <SelectItem value="online">Online Payment Only</SelectItem>
                  <SelectItem value="both">Both COD & Online</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(settings?.payment_mode === 'online' || settings?.payment_mode === 'both') && (
              <div className="border-t border-border pt-4 mt-4">
                <h4 className="font-medium mb-3">Razorpay Configuration</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Razorpay Key ID</Label>
                    <Input
                      type="text"
                      value={settings?.razorpay_key_id || ''}
                      onChange={(e) => setSettings({...settings, razorpay_key_id: e.target.value})}
                      placeholder="rzp_live_xxxxxxxxxx"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Razorpay Key Secret</Label>
                    <Input
                      type="password"
                      value={settings?.razorpay_key_secret || ''}
                      onChange={(e) => setSettings({...settings, razorpay_key_secret: e.target.value})}
                      placeholder="********"
                      className="mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Get your Razorpay keys from <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener" className="text-primary underline">dashboard.razorpay.com</a>
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Map Provider</h3>
              <p className="text-sm text-muted-foreground">Choose your map service provider</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Map Provider</Label>
              <Select 
                value={settings?.map_provider || 'free'} 
                onValueChange={(v) => setSettings({...settings, map_provider: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">OpenStreetMap (Free)</SelectItem>
                  <SelectItem value="google">Google Maps (Premium)</SelectItem>
                </SelectContent>
              </Select>
              {settings?.map_provider === 'free' && (
                <p className="text-xs text-muted-foreground mt-2">
                  Uses OpenStreetMap with Leaflet.js - free and open source
                </p>
              )}
            </div>

            {settings?.map_provider === 'google' && (
              <div className="border-t border-border pt-4 mt-4">
                <h4 className="font-medium mb-3">Google Maps Configuration</h4>
                <div>
                  <Label>Google Maps API Key</Label>
                  <Input
                    type="password"
                    value={settings?.google_maps_api_key || ''}
                    onChange={(e) => setSettings({...settings, google_maps_api_key: e.target.value})}
                    placeholder="AIzaSy..."
                    className="mt-1"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Get your API key from <a href="https://console.cloud.google.com/apis" target="_blank" rel="noopener" className="text-primary underline">Google Cloud Console</a>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* WhatsApp Support Section */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">WhatsApp Support</h3>
              <p className="text-sm text-muted-foreground">Configure support contacts for customers and riders</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Customer Support */}
            <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-medium text-blue-700 dark:text-blue-300 flex items-center gap-2">
                <User className="w-4 h-4" />
                Customer Support
              </h4>
              <div>
                <Label>Display Name</Label>
                <Input
                  type="text"
                  value={customerSupportName}
                  onChange={(e) => setCustomerSupportName(e.target.value)}
                  placeholder="e.g., BizilGO Support"
                  className="mt-1"
                  data-testid="customer-support-name-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Name shown to customers (defaults to your name)
                </p>
              </div>
              <div>
                <Label>WhatsApp Number</Label>
                <Input
                  type="tel"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="91XXXXXXXXXX (with country code)"
                  className="mt-1"
                  data-testid="whatsapp-number-input"
                />
              </div>
              <div>
                <Label>Default Message</Label>
                <Input
                  type="text"
                  value={supportMessage}
                  onChange={(e) => setSupportMessage(e.target.value)}
                  placeholder="I need help with my booking"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Rider Support */}
            <div className="space-y-4 p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <h4 className="font-medium text-orange-700 dark:text-orange-300 flex items-center gap-2">
                <Bike className="w-4 h-4" />
                Rider Support
              </h4>
              <div>
                <Label>Display Name</Label>
                <Input
                  type="text"
                  value={riderSupportName}
                  onChange={(e) => setRiderSupportName(e.target.value)}
                  placeholder="e.g., Rider Helpdesk"
                  className="mt-1"
                  data-testid="rider-support-name-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Name shown to riders (defaults to your name)
                </p>
              </div>
              <div>
                <Label>WhatsApp Number</Label>
                <Input
                  type="tel"
                  value={riderWhatsappNumber}
                  onChange={(e) => setRiderWhatsappNumber(e.target.value)}
                  placeholder="91XXXXXXXXXX (with country code)"
                  className="mt-1"
                  data-testid="rider-whatsapp-number-input"
                />
              </div>
              <div>
                <Label>Default Message</Label>
                <Input
                  type="text"
                  value={riderSupportMessage}
                  onChange={(e) => setRiderSupportMessage(e.target.value)}
                  placeholder="I need help with my ride"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <Button 
            onClick={handleSaveSupport} 
            disabled={savingSupport}
            className="mt-6"
            data-testid="save-support-btn"
          >
            {savingSupport ? "Saving..." : "Save Support Settings"}
          </Button>
        </div>

        {/* Tiered Notification Settings */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Ride Request Notification</h3>
              <p className="text-sm text-muted-foreground">Configure how ride requests are sent to drivers</p>
            </div>
          </div>

          <div className="space-y-4 bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              <strong>How it works:</strong> When a customer books a ride, nearby drivers are notified first. 
              If no one accepts within the delay time, remaining drivers in the extended range are notified.
              If no drivers are within the first tier distance, ALL drivers are notified immediately.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>First Tier Distance (km)</Label>
                <Input
                  type="number"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={tier1Distance}
                  onChange={(e) => setTier1Distance(e.target.value)}
                  className="mt-1"
                  data-testid="tier1-distance-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Drivers within this distance get notified first (default: 2km)
                </p>
              </div>
              <div>
                <Label>Extended Range Delay (seconds)</Label>
                <Input
                  type="number"
                  min="5"
                  max="60"
                  step="5"
                  value={tier2Delay}
                  onChange={(e) => setTier2Delay(e.target.value)}
                  className="mt-1"
                  data-testid="tier2-delay-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Wait time before notifying remaining drivers (default: 15s)
                </p>
              </div>
            </div>
            
            <Button 
              onClick={handleSaveTieredSettings} 
              disabled={savingTieredSettings}
              variant="outline"
              className="mt-2"
              data-testid="save-tiered-settings-btn"
            >
              {savingTieredSettings ? "Saving..." : "Save Notification Settings"}
            </Button>
          </div>
        </div>

        {/* External Services Section (More Services) */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <ExternalLink className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">More Services</h3>
              <p className="text-sm text-muted-foreground">Configure external service links shown to customers</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Bizil Food */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-orange-500" />
                  <Label className="font-medium">Bizil Food</Label>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Enabled</span>
                  <Switch
                    checked={externalServices.bizil_food_enabled}
                    onCheckedChange={(checked) => setExternalServices({...externalServices, bizil_food_enabled: checked})}
                    data-testid="bizil-food-enabled-switch"
                  />
                </div>
              </div>
              <Input
                type="url"
                value={externalServices.bizil_food_url || ""}
                onChange={(e) => setExternalServices({...externalServices, bizil_food_url: e.target.value})}
                placeholder="https://yourfoodapp.com"
                className="mt-1"
                data-testid="bizil-food-url-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Link to your food delivery service
              </p>
            </div>

            {/* Farmster */}
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Tractor className="w-5 h-5 text-green-500" />
                  <Label className="font-medium">Farmster</Label>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Enabled</span>
                  <Switch
                    checked={externalServices.farmster_enabled}
                    onCheckedChange={(checked) => setExternalServices({...externalServices, farmster_enabled: checked})}
                    data-testid="farmster-enabled-switch"
                  />
                </div>
              </div>
              <Input
                type="url"
                value={externalServices.farmster_url || ""}
                onChange={(e) => setExternalServices({...externalServices, farmster_url: e.target.value})}
                placeholder="https://yourfarmingapp.com"
                className="mt-1"
                data-testid="farmster-url-input"
                disabled={!externalServices.farmster_enabled}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {externalServices.farmster_enabled 
                  ? "Link to your farm equipment rental service" 
                  : "Enable to set URL. Shows 'Coming Soon' when disabled."}
              </p>
            </div>

            <Button 
              onClick={handleSaveExternalServices} 
              disabled={savingExternal}
              variant="outline"
              className="mt-2"
              data-testid="save-external-services-btn"
            >
              {savingExternal ? "Saving..." : "Save External Services"}
            </Button>
          </div>
        </div>

        {/* Data Sync Utilities Section */}
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Data Sync Utilities</h3>
              <p className="text-sm text-muted-foreground">Fix data inconsistencies between collections</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Food Delivery Orders Sync */}
            <div className="p-4 bg-muted rounded-lg space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground flex items-center gap-2">
                    <Utensils className="w-4 h-4 text-orange-500" />
                    Sync Food Delivery Orders
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fixes orders showing wrong status. Select a date range to process specific orders.
                  </p>
                </div>
              </div>
              
              {/* Date Range Selector */}
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[140px]">
                  <Label className="text-xs text-muted-foreground">From Date</Label>
                  <Input
                    type="date"
                    value={syncDateFrom}
                    onChange={(e) => setSyncDateFrom(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1 min-w-[140px]">
                  <Label className="text-xs text-muted-foreground">To Date</Label>
                  <Input
                    type="date"
                    value={syncDateTo}
                    onChange={(e) => setSyncDateTo(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button 
                  onClick={handleSyncFoodDeliveryOrders}
                  disabled={syncing}
                  variant="outline"
                  className="shrink-0"
                  data-testid="sync-food-orders-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? "Syncing..." : "Run Sync"}
                </Button>
              </div>
              
              {lastSyncResult && !lastSyncResult.error && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium text-sm">Sync Complete - Fixed {lastSyncResult.total_synced || 0} records</span>
                  </div>
                  <p className="text-xs mt-1 text-muted-foreground">
                    {lastSyncResult.date_range && <span>Range: {lastSyncResult.date_range}</span>}
                  </p>
                  {lastSyncResult.details && (
                    <div className="text-xs mt-2 space-y-1">
                      <p>• Delivered status fixed: <strong>{lastSyncResult.details.delivered_status_fixed || 0}</strong></p>
                      <p>• Cancelled status fixed: <strong>{lastSyncResult.details.cancelled_status_fixed || 0}</strong></p>
                      <p>• Assigned status fixed: <strong>{lastSyncResult.details.assigned_status_fixed || 0}</strong></p>
                      <p>• Rides→QC synced: <strong>{lastSyncResult.details.rides_to_qc_synced || 0}</strong></p>
                      <p>• QC→Rides synced: <strong>{lastSyncResult.details.qc_to_rides_synced || 0}</strong></p>
                    </div>
                  )}
                </div>
              )}
              
              {lastSyncResult?.error && (
                <div className="p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-600 dark:text-red-400">{lastSyncResult.error}</p>
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              Tip: Start with a small date range (1-3 days) and expand if needed.
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="min-w-32">
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>

      {/* Phone Change Dialog */}
      <Dialog open={showPhoneChangeDialog} onOpenChange={(open) => !open && resetPhoneChangeDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {phoneChangeStep === 1 && "Change Login Phone Number"}
              {phoneChangeStep === 3 && "Verify New Phone Number"}
            </DialogTitle>
            <DialogDescription>
              {phoneChangeStep === 1 && "Enter your new phone number. You will need to verify it with OTP."}
              {phoneChangeStep === 3 && "Enter the OTP sent to your new phone number."}
            </DialogDescription>
          </DialogHeader>

          {phoneChangeStep === 1 && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800 text-sm">Important</p>
                    <p className="text-xs text-yellow-700">
                      After changing your phone number, you will use the new number to log in. 
                      All your data and settings will remain intact.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Current Phone Number</Label>
                <Input value={user?.phone || ""} disabled className="mt-1 bg-muted" />
              </div>

              <div>
                <Label>New Phone Number *</Label>
                <Input
                  type="tel"
                  maxLength={10}
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit phone number"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Confirm New Phone Number *</Label>
                <Input
                  type="tel"
                  maxLength={10}
                  value={confirmPhone}
                  onChange={(e) => setConfirmPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Re-enter phone number to confirm"
                  className="mt-1"
                />
                {confirmPhone && newPhone && confirmPhone !== newPhone && (
                  <p className="text-xs text-destructive mt-1">Phone numbers don't match</p>
                )}
                {confirmPhone && newPhone && confirmPhone === newPhone && newPhone.length === 10 && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Phone numbers match
                  </p>
                )}
              </div>
            </div>
          )}

          {phoneChangeStep === 3 && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                <p className="text-sm text-blue-800">
                  OTP sent to <strong>{newPhone}</strong>
                </p>
                <p className="text-xs text-blue-600 mt-1">Enter the 6-digit code sent via SMS</p>
              </div>

              <div>
                <Label>Enter OTP</Label>
                <Input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit OTP"
                  className="mt-1 text-center text-2xl tracking-widest"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={resetPhoneChangeDialog}>Cancel</Button>
            {phoneChangeStep === 1 && (
              <Button 
                onClick={handleInitiatePhoneChange} 
                disabled={!newPhone || !confirmPhone || newPhone !== confirmPhone || newPhone.length !== 10}
              >
                Continue
              </Button>
            )}
            {phoneChangeStep === 3 && (
              <Button 
                onClick={handleVerifyOTP} 
                disabled={otp.length !== 6 || changingPhone}
              >
                {changingPhone ? "Verifying..." : "Verify & Change"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Final Confirmation Alert */}
      <AlertDialog open={showFinalConfirm} onOpenChange={setShowFinalConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Phone Number Change</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>You are about to change your login phone number:</p>
              <div className="p-3 bg-muted rounded-lg my-3">
                <p className="text-sm"><span className="text-muted-foreground">From:</span> <strong>{user?.phone}</strong></p>
                <p className="text-sm"><span className="text-muted-foreground">To:</span> <strong>{newPhone}</strong></p>
              </div>
              <p className="text-sm">
                After this change, you will need to use <strong>{newPhone}</strong> to log in. 
                All your data, settings, cities, and drivers will remain unchanged.
              </p>
              <p className="text-sm font-medium text-amber-600">
                Are you sure you want to proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAndSendOTP} disabled={changingPhone}>
              {changingPhone ? "Sending OTP..." : "Yes, Send OTP"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSettingsManagement;
